import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Grid 
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase.js'; // Adjust path to your firebase config
import { useFirebase } from './contexts/FirebaseContext.js';

function Collections({ recipes }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [logos, setLogos] = useState({});
  const [loading, setLoading] = useState(true);
  const [userRecipeCount, setUserRecipeCount] = useState(0);
  const { user, loading: userLoading } = useFirebase();

  // Helper function to check if cache is stale
  const shouldRefreshCache = (cacheKey, expiryMinutes = 60) => {
    const cachedTimestamp = localStorage.getItem(`${cacheKey}_timestamp`);
    if (!cachedTimestamp) return true;
    
    const now = new Date().getTime();
    const cachedTime = parseInt(cachedTimestamp, 10);
    const minutesPassed = (now - cachedTime) / (1000 * 60);
    
    return minutesPassed > expiryMinutes;
  };

  // Fetch logos from Firestore with caching
  useEffect(() => {
    const fetchLogos = async () => {
      setLoading(true);
      
      // Check cache first
      const cachedLogos = localStorage.getItem('collectionLogos');
      const needsRefresh = shouldRefreshCache('collectionLogos', 240); // 4 hour cache for logos
      
      if (cachedLogos && !needsRefresh) {
        console.log('Using cached collection logos');
        setLogos(JSON.parse(cachedLogos));
        setLoading(false);
        return;
      }

      try {
        console.log('Fetching fresh collection logos from Firestore');
        const querySnapshot = await getDocs(collection(db, 'collectionLogos'));
        const logosData = {};
        querySnapshot.forEach(doc => {
          const data = doc.data();
          logosData[doc.id] = data.imageUrl || 'https://via.placeholder.com/300'; // Fallback if imageUrl missing
        });
        
        // Cache the logos
        localStorage.setItem('collectionLogos', JSON.stringify(logosData));
        localStorage.setItem('collectionLogos_timestamp', new Date().getTime().toString());
        
        setLogos(logosData);
      } catch (error) {
        console.error('Error fetching collection logos:', error);
        // Try to use cache even if expired
        if (cachedLogos) {
          setLogos(JSON.parse(cachedLogos));
        } else {
          setLogos({});
        }
      } finally {
        setLoading(false);
      }
    };
    fetchLogos();
  }, []);

  // Fetch user's recipe count with caching
  useEffect(() => {
    const fetchUserRecipes = async () => {
      if (userLoading) {
        return;
      }

      if (!user) {
        setUserRecipeCount(0);
        return;
      }

      // Check cache first for user recipe count
      const cachedCount = localStorage.getItem(`userRecipeCount_${user.uid}`);
      const needsRefresh = shouldRefreshCache(`userRecipeCount_${user.uid}`, 5); // 5 minute cache
      
      if (cachedCount && !needsRefresh) {
        console.log('Using cached user recipe count');
        setUserRecipeCount(parseInt(cachedCount, 10));
        return;
      }

      try {
        console.log('Fetching fresh user recipe count from Firestore');
        const querySnapshot = await getDocs(collection(db, `users/${user.uid}/recipes`));
        const count = querySnapshot.size;
        
        // Cache the count
        localStorage.setItem(`userRecipeCount_${user.uid}`, count.toString());
        localStorage.setItem(`userRecipeCount_${user.uid}_timestamp`, new Date().getTime().toString());
        
        setUserRecipeCount(count);
      } catch (error) {
        console.error('Error fetching user recipes:', error);
        // If cached count exists, use it even if expired
        if (cachedCount) {
          setUserRecipeCount(parseInt(cachedCount, 10));
        } else {
          setUserRecipeCount(0);
        }
      }
    };

    fetchUserRecipes();
  }, [user, userLoading]);

  // Derive collections from recipes, enhanced with Firestore logos
  const collections = useMemo(() => {
    // Add My Recipes as a special collection at the start
    const myRecipes = {
      name: 'My Recipes',
      image: logos['myRecipes'] || 'https://via.placeholder.com/300', // Use the logo from collectionLogos
      recipeCount: userRecipeCount,
      isUserCollection: true
    };

    // Remove 'All Recipes' from origins since it's now in the Action Filters at the top of the page
    const origins = new Set([...recipes.map(recipe => recipe.origin || 'Unknown')].filter(origin => 
      origin !== 'My Recipes' && origin !== 'All Recipes'
    ));
    
    const originCollections = Array.from(origins).sort().map(origin => ({
      name: origin,
      image: logos[origin] || 'https://via.placeholder.com/300',
      recipeCount: recipes.filter(r => r.origin === origin).length,
      isUserCollection: false
    }));

    return [myRecipes, ...originCollections];
  }, [recipes, logos, userRecipeCount]);

  if (loading || userLoading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">Loading Collections...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      minHeight: '100%',
      p: 0,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Typography 
        variant="h5" 
        sx={{ 
          fontWeight: 500,
          fontSize: { xs: '1.5rem', sm: '1.75rem' },
          mb: { xs: 2, sm: 2.5 },
          px: { xs: 0, sm: 0 }
        }}
      >
        Collections
      </Typography>
      <Grid container spacing={2}>
        {collections.map(collection => (
          <Grid item xs={6} sm={4} md={3} key={collection.name}>
            <Box
              component={Link}
              to={collection.isUserCollection ? '/recipes/my-recipes' : collection.name === 'All Recipes' ? '/recipes/all' : `/recipes/collection/${encodeURIComponent(collection.name)}`}
              sx={{
                textDecoration: 'none',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.03)',
                },
              }}
            >
              <Box
                component="img"
                src={collection.image}
                alt={collection.name}
                sx={{
                  width: '100%',
                  objectFit: 'cover',
                  aspectRatio: '1 / 1', // Strictly enforce square aspect ratio
                  borderRadius: theme.shape.borderRadius * 2, // Rounded corners
                  boxShadow: theme.shadows[4], // Add a subtle shadow for depth
                }}
              />
              <Box sx={{ p: { xs: 1, sm: 1.5 }, textAlign: 'center' }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 500,
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    color: theme.palette.text.primary,
                    lineHeight: 1.2
                  }}
                >
                  {collection.name}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}
                >
                  {collection.recipeCount} {collection.recipeCount === 1 ? 'recipe' : 'recipes'}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default Collections;